import { Button, Table } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { config } from "../config/config";
import { getProviderClients } from "../config/url";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import formatDate from "../utils/formatDate";
import axios from "axios";
import { toast } from "react-toastify";
import { FaSearch, FaCalendarAlt, FaTimesCircle } from "react-icons/fa";

// Custom hook for fetching provider clients (server-side pagination)
const useProviderClients = ({
  providerId,
  businessId,
  businessIdentifier,
  page,
  limit,
  search,
  status,
  startDate,
  endDate,
}) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!providerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({
          providerId,
          businessId: businessId || "",
          businessIdentifier: String(businessIdentifier),
          limit: String(limit),
          skip: String((page - 1) * limit),
        });
        if (search && search.trim()) params.set("search", search.trim());
        if (status && status !== "All") params.set("status", status);
        if (startDate && endDate) {
          const fmt = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
          };
          params.set("startDate", fmt(startDate));
          params.set("endDate", fmt(endDate));
        }
        const response = await axios.get(
          `${getProviderClients}?${params.toString()}`,
          config
        );
        setData(response.data.data || []);
        setTotal(response.data.total ?? (response.data.data || []).length);
      } catch (error) {
        console.error('Error fetching provider clients:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [providerId, businessId, businessIdentifier, page, limit, search, status, startDate, endDate]);

  return { data, total, loading, error };
};

const useCareManagerClients = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const user = useSelector((state) => state?.user);
  const authToken = user?.user?.token;
  const isCareManager =
    user?.user?.role === "careManager" ||
    user?.user?.providerType === "Care Manager";

  useEffect(() => {
    const fetchData = async () => {
      if (!authToken || !isCareManager) {
        setData(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/users/getCareManagerClients`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        setData(response.data.data);
      } catch (fetchError) {
        console.error("Error fetching care manager clients:", fetchError);
        setError(fetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, isCareManager]);

  return { data, loading, error };
};

const Clients = () => {
  const user = useSelector((state) => state?.user);
  const navigate = useNavigate();

  // State variables
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("Active");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [clientsPerPage] = useState(10);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [totalClients, setTotalClients] = useState(0);

  // User data
  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;
  const providerId = user.user?.providerId;
  const isCareManager =
    user?.user?.role === "careManager" ||
    user?.user?.providerType === "Care Manager";

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filter, startDate, endDate]);

  // Fetch client data (paginated for providers)
  const { data, total, loading, error } = useProviderClients({
    providerId: isCareManager ? null : providerId,
    businessId,
    businessIdentifier,
    page: currentPage,
    limit: clientsPerPage,
    search: debouncedSearch,
    status: filter,
    startDate,
    endDate,
  });

  const careManagerClients = useCareManagerClients();
  const currentClients = isCareManager
    ? (careManagerClients.data || []).slice(
        (currentPage - 1) * clientsPerPage,
        currentPage * clientsPerPage
      )
    : data || [];
  const pageLoading = isCareManager ? careManagerClients.loading : loading;
  const pageError = isCareManager ? careManagerClients.error : error;

  useEffect(() => {
    if (isCareManager) {
      setTotalClients((careManagerClients.data || []).length);
    } else {
      setTotalClients(total || 0);
    }
  }, [isCareManager, careManagerClients.data, total]);

  // Handle clicking outside date picker to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.react-datepicker') && !event.target.closest('.form-control')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Handle loading state
  if (pageLoading) {
    return (
      <div className="app-page">
        <div className="bg-white rounded shadow-sm p-5 text-center">
          <h2 className="app-page-title">
            Loading Clients...
          </h2>
          <div className="spinner-border text-primary mt-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (pageError) {
    return (
      <div className="app-page">
        <div className="bg-white rounded shadow-sm p-5 text-center">
          <h2
            style={{
              fontFamily: "Poppins",
              fontSize: "21px",
              fontWeight: 600,
              color: "#dc3545",
            }}
          >
            Error Loading Clients
          </h2>
          <p className="text-muted mt-2">
            {pageError.message || "An error occurred while loading clients. Please try again."}
          </p>
          <button 
            className="btn btn-primary mt-3"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const clearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setShowDatePicker(false);
  };

  // Handle loading state
  if (!data) {
    return (
      <div className="app-page">
        <div className="bg-white rounded shadow-sm p-5 text-center">
          <h2 className="app-page-title" style={{ marginBottom: "20px" }}>
            My Clients
          </h2>
          <p style={{ fontSize: "16px", color: "#666" }}>Loading clients...</p>
        </div>
      </div>
    );
  }

  // Handle empty client list
  if (data.length === 0) {
    return (
      <div className="app-page">
        <div className="bg-white rounded shadow-sm p-5 text-center">
          <h2 className="app-page-title" style={{ marginBottom: "20px" }}>
            My Clients
          </h2>
          <p style={{ fontSize: "16px", color: "#666" }}>
            You have no clients assigned at this time.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="bg-white rounded shadow-sm">
        <div className="app-page-toolbar">
          <h2 className="app-page-title">My Clients</h2>
          <div className="app-page-toolbar-filters d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
            <div className="app-filter-search-wrap">
              <FaSearch className="app-filter-search-icon" />
              <input
                className="form-control app-filter-search"
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name"
                type="text"
                value={search}
              />
            </div>

            {/* Last Session Date Filter */}
            <div className="position-relative" style={{ width: "fit-content" }}>
              <div
                className="form-control app-filter-control"
                onClick={() => setShowDatePicker(!showDatePicker)}
                style={{ minWidth: "200px" }}
              >
                <span>
                  {startDate && endDate
                    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                    : "Last Session Date"}
                </span>
                <span className="d-inline-flex align-items-center gap-2">
                  <FaCalendarAlt size={13} style={{ opacity: 0.7 }} />
                  {(startDate || endDate) && (
                    <FaTimesCircle
                      size={13}
                      style={{ cursor: "pointer", opacity: 0.7 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearDateFilter();
                      }}
                    />
                  )}
                </span>
              </div>
              {showDatePicker && (
                <div
                  className="position-absolute bg-white shadow-sm rounded p-2 mt-1 z-index-1000"
                  style={{ zIndex: 1000 }}
                >
                  <div className="d-flex flex-column">
                    <div className="mb-2">
                      <label className="mb-1">Start Date</label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        className="form-control"
                        placeholderText="Start Date"
                        dateFormat="MM/dd/yyyy"
                      />
                    </div>
                    <div className="mb-2">
                      <label className="mb-1">End Date</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        className="form-control"
                        placeholderText="End Date"
                        dateFormat="MM/dd/yyyy"
                      />
                    </div>
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          clearDateFilter();
                          setShowDatePicker(false);
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowDatePicker(false)}
                        style={{ backgroundColor: "#064260" }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <select
              className="form-select app-filter-select"
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: "150px" }}
              value={filter}
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
        <Table
          className="mb-0"
          hover
          responsive
          style={{ borderCollapse: "collapse", borderSpacing: 0 }}
        >
          <thead
            style={{
              backgroundColor: "#064260",
              color: "white",
              height: "50px",
              textAlign: "left",
            }}
          >
            <tr>
              <th>User ID</th>
              <th>Name</th>
              <th>DOB</th>
              <th>Last Session Date</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody
            style={{
              color: "#575757",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              textAlign: "left",
            }}
            >
            {currentClients.length > 0 ?
              currentClients.map((client) => {
                const isClientActive = client.status === "Active";
                return (
                  <tr key={client.id} style={{ backgroundColor: "white" }}>
                    <td>{client.id.toString().padStart(4, "0")}</td>
                    <td>{client.name || "N/A"}</td>
                    <td>{formatDate(client.dob) || "N/A"}</td>
                    <td>
                      {client.latestAppointmentTime === "N/A"
                        ? "N/A"
                        : formatDate(client.latestAppointmentTime) || "N/A"}
                    </td>
                    <td>{client.status || (isClientActive ? "Active" : "Inactive")}</td>
                    <td>
                      <Button
                        className={`app-view-btn${!isClientActive ? " is-disabled" : ""}`}
                        onClick={() => {
                          if (isClientActive)
                            navigate(`/clients/${client._id || client.id}`, {
                              state: {
                                client: {
                                  ...client,
                                  _id: client._id || client.id, // Use the actual user ID
                                  id: client.id
                                }
                              },
                            })
                          else {
                            toast.error("This client is no longer active on the platform.")
                          }
                        }}
                      // disabled={!isClientActive}
                      >
                        View
                      </Button>
                    </td>
                  </tr>)
              }) :
              <tr>
                <td colSpan="5" className="text-center text-muted py-5">
                  <div style={{ fontSize: "18px", color: "#6c757d" }}>
                    You have no clients assigned at this time.
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </Table>
        <TablePaginationBar
          currentPage={currentPage}
          totalItems={totalClients}
          rowsPerPage={clientsPerPage}
          onPageChange={paginate}
        />
      </div>
    </div>
  );
};

export { useProviderClients, useCareManagerClients };
export default Clients;